<template>
  <div v-if="totalItems > 0" class="pagination-container mt-4">
    <nav aria-label="Posts pagination">
      <ul class="pagination justify-content-center">
        <!-- Previous Button -->
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button
            class="page-link"
            @click="$emit('page-change', currentPage - 1)"
            :disabled="currentPage === 1"
          >
            <i class="bi bi-chevron-left"></i> Previous
          </button>
        </li>

        <!-- First Page -->
        <li v-if="currentPage > 3" class="page-item">
          <button class="page-link" @click="$emit('page-change', 1)">1</button>
        </li>
        <li v-if="currentPage > 4" class="page-item disabled">
          <span class="page-link">...</span>
        </li>

        <!-- Page Numbers -->
        <li
          v-for="page in visiblePages"
          :key="page"
          class="page-item"
          :class="{ active: currentPage === page }"
        >
          <button class="page-link" @click="$emit('page-change', page)">
            {{ page }}
          </button>
        </li>

        <!-- Last Page -->
        <li v-if="currentPage < totalPages - 3" class="page-item disabled">
          <span class="page-link">...</span>
        </li>
        <li v-if="currentPage < totalPages - 2" class="page-item">
          <button class="page-link" @click="$emit('page-change', totalPages)">{{ totalPages }}</button>
        </li>

        <!-- Next Button -->
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <button
            class="page-link"
            @click="$emit('page-change', currentPage + 1)"
            :disabled="currentPage === totalPages"
          >
            Next <i class="bi bi-chevron-right"></i>
          </button>
        </li>
      </ul>
    </nav>

    <!-- Page Info -->
    <div class="text-center mt-2">
      <small class="text-muted">
        Showing {{ startIndex + 1 }} - {{ Math.min(endIndex, totalItems) }} of {{ totalItems }} posts
      </small>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaginationControls',
  props: {
    currentPage: {
      type: Number,
      required: true
    },
    totalPages: {
      type: Number,
      required: true
    },
    totalItems: {
      type: Number,
      required: true
    },
    itemsPerPage: {
      type: Number,
      required: true
    }
  },
  emits: ['page-change'],
  computed: {
    visiblePages() {
      const pages = []
      const range = 2 // Show 2 pages on each side of current page

      let start = Math.max(1, this.currentPage - range)
      let end = Math.min(this.totalPages, this.currentPage + range)

      // Adjust range if we're near the beginning or end
      if (this.currentPage <= range + 1) {
        end = Math.min(this.totalPages, 5)
      }
      if (this.currentPage >= this.totalPages - range) {
        start = Math.max(1, this.totalPages - 4)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage
    },
    endIndex() {
      return this.startIndex + this.itemsPerPage
    }
  }
}
</script>

<style scoped>
.pagination-container {
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.pagination {
  margin-bottom: 0;
}

.page-link {
  color: #667eea;
  border: 1px solid #dee2e6;
  padding: 0.5rem 0.75rem;
  margin: 0 0.25rem;
  border-radius: 5px;
  transition: all 0.3s ease;
}

.page-link:hover {
  background-color: #667eea;
  color: white;
  border-color: #667eea;
}

.page-item.active .page-link {
  background-color: #667eea;
  border-color: #667eea;
  color: white;
  font-weight: bold;
}

.page-item.disabled .page-link {
  color: #6c757d;
  pointer-events: none;
  background-color: #fff;
  border-color: #dee2e6;
}
</style>
